/**
 * @fileoverview Pure thread-derivation helpers for `ThreadView` (MOR-529).
 *
 * Extracted so the reply-target selection, default-recipient, and
 * header-role grouping logic can be unit tested without mounting the
 * (very large) component. Behaviour-preserving extraction only — no logic
 * changes.
 */

import type { MailMessage, ThreadParticipant } from "./types";

/** Latest inbound message (preferred reply target), else the last message. */
export function selectReplySource(messages: MailMessage[]): MailMessage | null {
  const inbound = [...messages]
    .reverse()
    .find((m) => m.direction === "inbound");
  return inbound ?? messages[messages.length - 1] ?? null;
}

/**
 * Default reply recipients: the reply source's sender plus its other
 * recipients, minus the mailbox's own address, deduped and trimmed.
 */
export function computeDefaultReplyTo(
  replySource: MailMessage | null,
  selfAddress: string,
): string[] {
  if (!replySource) return [];
  const self = selfAddress.trim().toLowerCase();
  const candidates = [
    replySource.fromAddress,
    ...replySource.toAddresses,
  ].filter((a) => a.trim().toLowerCase() !== self);
  return [...new Set(candidates.map((a) => a.trim()))].filter(Boolean);
}

/**
 * Adds `incoming` addresses to `current`, keeping `current`'s order and
 * entries. Case-insensitive; skips blanks and anything in `exclude`. Used
 * when Reply All resumes a draft, so the wider recipient set is added
 * without dropping the user's own edits.
 */
export function mergeAddresses(
  current: string[],
  incoming: string[],
  exclude: string[] = [],
): string[] {
  const key = (a: string) => a.trim().toLowerCase();
  const taken = new Set([...current, ...exclude].map(key));
  const additions = incoming
    .map((a) => a.trim())
    .filter(Boolean)
    .filter((a, i, all) => all.findIndex((b) => key(b) === key(a)) === i)
    .filter((a) => !taken.has(key(a)));
  return [...current, ...additions];
}

/** Up to 3 unique contact roles across a thread's participants, for the header chips. */
export function computeHeaderRoles(
  participants: ThreadParticipant[],
): string[] {
  return [...new Set(participants.flatMap((p) => p.roles))].slice(0, 3);
}

/** Message ids expanded by default when a thread first loads: just the latest. */
export function computeDefaultExpandedIds(
  lastMessageId: string | undefined,
): Set<string> {
  return new Set(lastMessageId ? [lastMessageId] : []);
}
